<template>
  <div class="survey-page">
    <!-- PHASE 1: the questionnaire -->
    <div v-show="phase === 'survey'" id="surveyContainer">
      <p v-if="submitError" class="notice-warn">{{ submitError }}</p>
      <SurveyComponent :model="survey" :key="currentLocale" />
    </div>

    <!-- PHASE 2: file upload (end of form, direct-to-S3 via FilePond) -->
    <div v-if="phase === 'upload'" class="upload-phase">
      <h2>{{ t({ fr: 'Téléversement des fichiers', en: 'File upload' }) }}</h2>
      <p class="upload-lead">
        {{ t({
          fr: 'Téléversez le jeu de données, le dictionnaire de données, tout document pertinent et, le cas échéant, votre licence existante. Vos fichiers sont envoyés de façon sécurisée.',
          en: 'Upload the dataset, the data dictionary, any relevant document and, if applicable, your existing licence. Your files are sent securely.'
        }) }}
      </p>

      <FileUploader v-if="sessionToken" :token="sessionToken" @update:files="onFilesChanged" />

      <p class="upload-count">
        {{ uploadedFiles.length }}
        {{ t({ fr: 'fichier(s) téléversé(s)', en: 'file(s) uploaded' }) }}
      </p>

      <p v-if="uploadedFiles.length === 0" class="notice-info">
        {{ t({
          fr: "Aucun fichier n'a encore été téléversé. Vous pouvez tout de même soumettre si vous fournissez vos données par un autre moyen (indiqué dans vos réponses).",
          en: 'No file has been uploaded yet. You may still submit if you are providing your data by another means (noted in your answers).'
        }) }}
      </p>

      <p v-if="submitError" class="notice-warn">{{ submitError }}</p>

      <div class="upload-actions">
        <button class="btn-secondary" :disabled="submitting" @click="phase = 'survey'">
          {{ t({ fr: 'Retour au questionnaire', en: 'Back to the form' }) }}
        </button>
        <button class="btn-primary" :disabled="submitting" @click="submitFinal">
          {{ submitting
            ? t({ fr: 'Envoi en cours…', en: 'Submitting…' })
            : t({ fr: 'Soumettre définitivement', en: 'Submit' }) }}
        </button>
      </div>
    </div>

    <!-- PHASE 3: confirmation -->
    <div v-if="phase === 'done'" class="done-phase">
      <div class="done-card">
        <h2>{{ t({ fr: 'Merci ! Votre soumission a été reçue.', en: 'Thank you! Your submission has been received.' }) }}</h2>
        <p>
          {{ t({ fr: 'Numéro de soumission :', en: 'Submission reference:' }) }}
          <code>{{ submissionId }}</code>
        </p>
        <p>
          {{ t({
            fr: "L'équipe d'AECOM examinera vos données et communiquera avec vous au besoin.",
            en: 'The AECOM team will review your data and contact you if needed.'
          }) }}
        </p>

        <div class="review-link-box">
          <p v-if="emailSent">
            <strong>{{ t({
              fr: 'Un courriel de confirmation vous a été envoyé',
              en: 'A confirmation email has been sent to you'
            }) }}</strong>
            {{ t({
              fr: 'avec un lien privé pour revoir votre soumission, ajouter des fichiers ou des commentaires, ou en faire une nouvelle.',
              en: 'with a private link to review your submission, add files or comments, or make another one.'
            }) }}
          </p>
          <p>
            {{ t({
              fr: 'Vous pouvez aussi conserver ce lien de revue :',
              en: 'You can also keep this review link:'
            }) }}
          </p>
          <p><a :href="reviewUrl" class="review-url">{{ reviewUrl }}</a></p>
        </div>

        <div class="done-actions">
          <router-link :to="{ path: '/review', query: { token: submissionId } }" class="btn-primary">
            {{ t({ fr: 'Revoir / compléter ma soumission', en: 'Review / complete my submission' }) }}
          </router-link>
          <router-link to="/" class="btn-secondary">
            {{ t({ fr: "Retour à l'accueil", en: 'Back to home' }) }}
          </router-link>
        </div>
      </div>
    </div>

    <!-- PHASE 4: minimum rights not granted — no file upload, we follow up -->
    <div v-if="phase === 'refused'" class="done-phase">
      <div class="done-card">
        <h2>{{ t({ fr: 'Merci. Vos réponses ont été enregistrées.', en: 'Thank you. Your answers have been recorded.' }) }}</h2>
        <p>
          {{ t({
            fr: "Puisque les droits minimaux requis n'ont pas été accordés, nous ne pouvons pas encore intégrer ces données au projet. Notre équipe communiquera avec vous pour discuter des enjeux que vous avez soulevés.",
            en: 'Since the required minimum rights were not granted, we cannot yet incorporate this data into the project. Our team will contact you to discuss the concerns you raised.'
          }) }}
        </p>
        <p v-if="submissionId">
          {{ t({ fr: 'Numéro de référence :', en: 'Reference number:' }) }}
          <code>{{ submissionId }}</code>
        </p>

        <div class="review-link-box" v-if="emailSent">
          <p>
            <strong>{{ t({
              fr: 'Un courriel de confirmation vous a été envoyé',
              en: 'A confirmation email has been sent to you'
            }) }}</strong>
            {{ t({
              fr: 'avec un lien privé pour revoir vos réponses.',
              en: 'with a private link to review your answers.'
            }) }}
          </p>
        </div>

        <div class="done-actions">
          <router-link to="/" class="btn-secondary">
            {{ t({ fr: "Retour à l'accueil", en: 'Back to home' }) }}
          </router-link>
        </div>
      </div>
    </div>

    <!-- PHASE 5: personal / sensitive information flagged — no file upload,
         data requiring special handling is reviewed before transfer -->
    <div v-if="phase === 'review_needed'" class="done-phase">
      <div class="done-card">
        <h2>{{ t({ fr: 'Merci. Vos réponses ont été enregistrées.', en: 'Thank you. Your answers have been recorded.' }) }}</h2>
        <p>
          {{ t({
            fr: "Puisque votre soumission pourrait contenir des renseignements personnels, confidentiels ou sensibles, veuillez ne pas téléverser les données à cette étape. AECOM communiquera avec vous pour discuter des prochaines étapes.",
            en: 'Since your submission may contain personal, confidential or sensitive information, please do not upload the data at this stage. AECOM will contact you to discuss next steps.'
          }) }}
        </p>
        <p v-if="submissionId">
          {{ t({ fr: 'Numéro de référence :', en: 'Reference number:' }) }}
          <code>{{ submissionId }}</code>
        </p>

        <div class="review-link-box" v-if="emailSent">
          <p>
            <strong>{{ t({
              fr: 'Un courriel de confirmation vous a été envoyé',
              en: 'A confirmation email has been sent to you'
            }) }}</strong>
            {{ t({
              fr: 'avec un lien privé pour revoir vos réponses.',
              en: 'with a private link to review your answers.'
            }) }}
          </p>
        </div>

        <div class="done-actions">
          <router-link to="/" class="btn-secondary">
            {{ t({ fr: "Retour à l'accueil", en: 'Back to home' }) }}
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { markRaw } from 'vue'
import { Model, surveyLocalization } from 'survey-core'
import { SurveyComponent } from 'survey-vue3-ui'
import 'survey-core/survey-core.css'
import 'survey-core/i18n/french'

// French is our fallback locale: question strings live under the `default`
// key (French) with `en` overrides. SurveyJS's built-in default locale is
// English, so without this, `default` (French) would still show when
// locale = 'en'. Declaring 'fr' as the default makes `default` map to
// French and 'en' resolve to the English overrides.
surveyLocalization.defaultLocale = 'fr'
import { surveyJson } from '../data/survey-questions.js'
import FileUploader from '../components/FileUploader.vue'
import { t, localeState } from '../i18n/index.js'
import {
  validateOrganisation,
  startSurvey,
  getTempSurvey,
  saveTempSurvey,
  completeSurvey,
  getSubmissionReview,
  notifyCompletion,
  saveSession,
  loadSession,
  clearSession
} from '../services/api.js'

export default {
  name: 'SurveyPage',
  components: { SurveyComponent, FileUploader },

  data () {
    // markRaw: keep the SurveyJS model OUT of Vue's reactive proxy.
    // survey-core (2.5+) drives its own rendering; wrapping it in a Vue
    // proxy breaks programmatic updates (survey.data = …, page changes).
    const survey = markRaw(new Model(surveyJson))
    survey.showCompletedPage = false // we drive the upload/confirmation phases
    survey.locale = localeState.current
    return {
      survey,
      sessionToken: null,
      phase: 'survey', // 'survey' | 'upload' | 'done' | 'refused' | 'review_needed'
      uploadedFiles: [],
      submitting: false,
      submitError: '',
      submissionId: '',
      reviewUrl: '',
      emailSent: false
    }
  },

  computed: {
    // Reactive handle on the global language toggle.
    currentLocale () {
      return localeState.current
    }
  },

  watch: {
    // Keep SurveyJS in sync with the language toggle. The :key on
    // SurveyComponent forces a re-render; the Model keeps its data and
    // current page, so nothing entered is lost.
    currentLocale (loc) {
      this.survey.locale = loc
      this.updateCompleteText(this.survey)
    }
  },

  mounted () {
    this.initSurvey()
  },

  methods: {
    t,

    async initSurvey () {
      // Render HTML in titles / choice texts / matrix rows.
      this.survey.onTextMarkdown.add((sender, options) => {
        options.html = options.text
      })

      // Resume an in-progress session. Priority: an explicit ?session=<token>
      // link (survives a cleared localStorage / another device), then the
      // token remembered in localStorage.
      const urlSession = (this.$route.query.session || '').toString()
      const stored = loadSession()
      let resumeToken = null
      let resumePage = 0

      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(urlSession)) {
        const saved = await getTempSurvey(urlSession)
        if (saved) {
          resumeToken = urlSession
          this.survey.data = saved
          resumePage = (stored && stored.token === urlSession) ? stored.page : 0
        }
      }
      if (!resumeToken && stored) {
        const saved = await getTempSurvey(stored.token)
        if (saved) {
          resumeToken = stored.token
          this.survey.data = saved
          resumePage = stored.page
        } else {
          clearSession()
        }
      }
      if (resumeToken) {
        this.sessionToken = resumeToken
        saveSession(resumeToken, resumePage)
        this.syncUrlSession()
        if (resumePage > 0 && resumePage < this.survey.visiblePages.length) {
          this.survey.currentPageNo = resumePage
        }
      }

      // Pre-fill answers from a previous submission (?from=<token>) so the
      // user can start a fresh submission based on what they sent before.
      // This is a NEW session — only the answers are copied, not the token.
      if (!this.sessionToken) {
        const fromToken = (this.$route.query.from || '').toString()
        if (fromToken) {
          const prev = await getSubmissionReview(fromToken)
          if (prev && prev.json_data) {
            this.survey.data = { ...prev.json_data }
          }
        }
      }

      // Pre-fill the access code from the URL (?code=ABC123) and validate.
      const urlCode = (this.$route.query.code || '').toString().trim().toUpperCase()
      if (!this.sessionToken && /^[A-Z0-9]{6}$/.test(urlCode)) {
        this.survey.setValue('identification_code', urlCode)
      }

      // Bulk data assignment (resume / pre-fill above) doesn't fire
      // onValueChanged, so sync the complete-button label once up front.
      this.updateCompleteText(this.survey)

      // Save partial data on every page navigation. Mint a session token
      // first if we don't yet have one (anonymous path).
      this.survey.onCurrentPageChanged.add(async (sender) => {
        await this.ensureSession(sender)
        if (this.sessionToken) {
          saveSession(this.sessionToken, sender.currentPageNo)
          await saveTempSurvey(this.sessionToken, sender.data)
        }
      })

      // Live validation + feedback for the access code.
      this.survey.onValueChanged.add(async (sender, options) => {
        if (options.name !== 'identification_code') return

        const raw = (options.value || '').trim()
        const code = raw.toUpperCase()
        if (code !== options.value) {
          sender.setValue('identification_code', code)
          return
        }

        const details = sender.getQuestionByName('organisation_details')

        if (code.length < 6) {
          if (details) details.visible = false
          return
        }
        if (!/^[A-Z0-9]{6}$/.test(code)) {
          if (details) {
            details.html = `<div class="property-not-found">${t({
              fr: "Le code d'accès doit contenir exactement 6 caractères (lettres ou chiffres).",
              en: 'The access code must contain exactly 6 characters (letters or digits).'
            })}</div>`
            details.visible = true
          }
          return
        }

        if (details) {
          details.html = `<p class="property-loading">${t({ fr: 'Validation du code en cours…', en: 'Validating code…' })}</p>`
          details.visible = true
        }

        const org = await validateOrganisation(code)
        if (org) {
          this.applySession(sender, org)
          // Persist current answers once now that a session exists, so a
          // refresh on the identification page (before advancing) resumes.
          if (this.sessionToken) await saveTempSurvey(this.sessionToken, sender.data)
          if (details) {
            details.html = `<div class="property-info"><strong>${t({ fr: 'Code valide', en: 'Valid code' })} :</strong><br>${this.escapeHtml(org.name)}${org.region ? ' — ' + this.escapeHtml(org.region) : ''}</div>`
            details.visible = true
          }
        } else {
          // Unknown code — do NOT clear an anonymous token if one exists.
          sender.setValue('is_resubmission', false)
          if (details) {
            details.html = `<div class="property-not-found">${t({
              fr: "Code d'accès non reconnu. Vérifiez le code, ou laissez le champ vide pour soumettre sans code.",
              en: 'Access code not recognized. Check the code, or leave the field empty to submit without a code.'
            })}</div>`
            details.visible = true
          }
        }
      })

      // Gate the identification page: a NON-EMPTY code must resolve to a
      // real organisation. An empty code is allowed (anonymous path).
      this.survey.onServerValidateQuestions.add(async (sender, options) => {
        // On later pages the token already exists — nothing to check.
        if (this.sessionToken && sender.currentPage.name !== 'identification') {
          options.complete()
          return
        }

        const code = (sender.data.identification_code || '').trim().toUpperCase()

        if (code.length === 0) {
          // No code → anonymous session.
          await this.ensureSession(sender)
          options.complete()
          return
        }

        if (!/^[A-Z0-9]{6}$/.test(code)) {
          options.errors.identification_code = t({
            fr: "Le code d'accès doit contenir exactement 6 caractères (lettres ou chiffres).",
            en: 'The access code must contain exactly 6 characters (letters or digits).'
          })
          options.complete()
          return
        }

        const org = await validateOrganisation(code)
        if (org) {
          this.applySession(sender, org)
        } else {
          options.errors.identification_code = t({
            fr: "Code d'accès non reconnu. Vérifiez le code, ou laissez le champ vide pour soumettre sans code.",
            en: 'Access code not recognized. Check the code, or leave the field empty to submit without a code.'
          })
        }
        options.complete()
      })

      // The "Complete" button reads differently depending on whether the
      // minimum rights (section 6.1) were granted, and whether personal or
      // sensitive information was flagged (section 11): both cases end the
      // form without a file-upload step.
      this.survey.onValueChanged.add((sender, options) => {
        if (!['grant_minimum_rights', 'no_personal_info', 'no_sensitive_info'].includes(options.name)) return
        this.updateCompleteText(sender)
      })

      // On completion: normally move to the file-upload phase (session
      // stays OPEN so uploads can be authorized; final submit happens
      // after upload). If the minimum rights were refused, or if personal /
      // sensitive information may be present, there is nothing to upload
      // yet — finalize immediately and stop, so the contributor cannot
      // proceed to file upload before AECOM has reviewed the situation.
      this.survey.onComplete.add(async (sender) => {
        await this.ensureSession(sender)
        if (this.sessionToken) {
          await saveTempSurvey(this.sessionToken, sender.data)
        }
        if (sender.getValue('grant_minimum_rights') === 'non') {
          await this.submitRefused(sender)
        } else if (this.needsReview(sender)) {
          await this.submitReviewNeeded(sender)
        } else {
          this.phase = 'upload'
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }
      })
    },

    // True when the contributor could not confirm the absence of personal
    // or sensitive information — data upload must wait for AECOM review.
    needsReview (sender) {
      const personalConfirmed = (sender.getValue('no_personal_info') || []).includes('confirmed')
      const sensitiveConfirmed = (sender.getValue('no_sensitive_info') || []).includes('confirmed')
      return !personalConfirmed || !sensitiveConfirmed
    },

    // NOTE: assigning a { default, en } locale object directly to a live
    // model's completeText does not go through SurveyJS's JSON-loc parser
    // (that only happens when the survey is built from JSON), so it must
    // be resolved to a plain string for the current locale here instead.
    updateCompleteText (sender) {
      sender.completeText = (sender.getValue('grant_minimum_rights') === 'non' || this.needsReview(sender))
        ? t({ fr: 'Terminer', en: 'Finish' })
        : t({ fr: 'Continuer vers le téléversement', en: 'Continue to file upload' })
    },

    // Mint an anonymous session token if none exists yet.
    async ensureSession (sender) {
      if (this.sessionToken) return
      const code = (sender.data.identification_code || '').trim().toUpperCase()
      if (/^[A-Z0-9]{6}$/.test(code)) {
        const org = await validateOrganisation(code)
        if (org) {
          this.applySession(sender, org)
          return
        }
      }
      const token = await startSurvey()
      if (token) {
        this.sessionToken = token
        saveSession(token, sender.currentPageNo)
        this.syncUrlSession()
      }
    },

    applySession (sender, org) {
      if (org.token && org.token !== this.sessionToken) {
        this.sessionToken = org.token
        saveSession(org.token, sender.currentPageNo)
        this.syncUrlSession()
      }
      sender.setValue('is_resubmission', !!org.resubmission)
    },

    // Reflect the current session token in the URL (?session=<token>) so a
    // refresh — or reopening the link on another device — resumes the form.
    // Uses replace() to avoid polluting the browser history.
    syncUrlSession () {
      if (!this.sessionToken) return
      if (this.$route.query.session === this.sessionToken) return
      this.$router
        .replace({ query: { ...this.$route.query, session: this.sessionToken } })
        .catch(() => { /* duplicate navigation — ignore */ })
    },

    onFilesChanged (files) {
      this.uploadedFiles = files
    },

    async submitFinal () {
      if (!this.sessionToken) {
        this.submitError = t({ fr: 'Session introuvable. Veuillez recharger la page.', en: 'Session not found. Please reload the page.' })
        return
      }
      this.submitting = true
      this.submitError = ''
      try {
        await completeSurvey(this.sessionToken, this.survey.data, this.uploadedFiles)
        this.submissionId = this.sessionToken
        this.reviewUrl = `${window.location.origin}/review?token=${this.sessionToken}`
        // Email the contributor a review link (best-effort; non-blocking
        // for the confirmation). emailSent stays false if SMTP is off.
        const notified = await notifyCompletion(this.sessionToken, window.location.origin, localeState.current)
        this.emailSent = !!(notified && notified.sent)
        clearSession()
        this.phase = 'done'
        window.scrollTo({ top: 0, behavior: 'smooth' })
      } catch (err) {
        this.submitError = t({
          fr: 'Une erreur est survenue lors de la soumission. Veuillez réessayer.',
          en: 'An error occurred while submitting. Please try again.'
        })
        console.error(err)
      } finally {
        this.submitting = false
      }
    },

    // Minimum rights (section 6.1) were refused: finalize with no files and
    // go straight to the "we will contact you" phase — no upload step.
    async submitRefused (sender) {
      if (!this.sessionToken) {
        this.submitError = t({ fr: 'Session introuvable. Veuillez recharger la page.', en: 'Session not found. Please reload the page.' })
        return
      }
      this.submitting = true
      this.submitError = ''
      try {
        await completeSurvey(this.sessionToken, sender.data, [])
        this.submissionId = this.sessionToken
        const notified = await notifyCompletion(this.sessionToken, window.location.origin, localeState.current)
        this.emailSent = !!(notified && notified.sent)
        clearSession()
        this.phase = 'refused'
        window.scrollTo({ top: 0, behavior: 'smooth' })
      } catch (err) {
        this.submitError = t({
          fr: 'Une erreur est survenue lors de la soumission. Veuillez réessayer.',
          en: 'An error occurred while submitting. Please try again.'
        })
        console.error(err)
      } finally {
        this.submitting = false
      }
    },

    // Personal / sensitive information may be present: finalize with no
    // files and go straight to the "please don't upload yet" phase — no
    // upload step, mirroring submitRefused.
    async submitReviewNeeded (sender) {
      if (!this.sessionToken) {
        this.submitError = t({ fr: 'Session introuvable. Veuillez recharger la page.', en: 'Session not found. Please reload the page.' })
        return
      }
      this.submitting = true
      this.submitError = ''
      try {
        await completeSurvey(this.sessionToken, sender.data, [])
        this.submissionId = this.sessionToken
        const notified = await notifyCompletion(this.sessionToken, window.location.origin, localeState.current)
        this.emailSent = !!(notified && notified.sent)
        clearSession()
        this.phase = 'review_needed'
        window.scrollTo({ top: 0, behavior: 'smooth' })
      } catch (err) {
        this.submitError = t({
          fr: 'Une erreur est survenue lors de la soumission. Veuillez réessayer.',
          en: 'An error occurred while submitting. Please try again.'
        })
        console.error(err)
      } finally {
        this.submitting = false
      }
    },

    escapeHtml (str) {
      if (!str) return ''
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
    }
  }
}
</script>

<style scoped>
.survey-page {
  max-width: 1000px;
  margin: 0 auto;
  padding: 30px 20px;
}

#surveyContainer,
.upload-phase,
.done-phase {
  padding: 30px 20px;
  background-color: var(--aecom-white);
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 53, 62, 0.1);
}

.upload-phase h2,
.done-card h2 {
  color: var(--aecom-midnight-teal);
  border-bottom: 3px solid var(--aecom-lime-green);
  padding-bottom: 12px;
  margin-top: 0;
}

.upload-lead {
  color: var(--aecom-midnight-teal);
  line-height: 1.6;
}

.upload-count {
  font-weight: 600;
  color: var(--aecom-earth-green);
}

.upload-actions {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 24px;
}

.btn-primary,
.btn-secondary {
  padding: 12px 28px;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  border: 2px solid var(--aecom-earth-green);
  transition: all 0.3s ease;
  text-decoration: none;
  display: inline-block;
}

.btn-primary {
  background: var(--aecom-earth-green);
  color: var(--aecom-white);
}

.btn-primary:hover:not(:disabled) {
  background: var(--aecom-midnight-teal);
  border-color: var(--aecom-midnight-teal);
}

.btn-secondary {
  background: transparent;
  color: var(--aecom-midnight-teal);
  border-color: var(--aecom-midnight-teal);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--aecom-midnight-teal);
  color: var(--aecom-white);
}

.btn-primary:disabled,
.btn-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.notice-info {
  background: #f0f7f5;
  border-left: 4px solid var(--aecom-earth-green);
  padding: 12px 16px;
  border-radius: 4px;
  color: var(--aecom-midnight-teal);
}

.notice-warn {
  background: #fff3f3;
  border-left: 4px solid var(--aecom-coral-orange, #e52713);
  padding: 12px 16px;
  border-radius: 4px;
  color: #b3200f;
}

.done-card code {
  background: var(--aecom-black-5, #f6f6f6);
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.95rem;
}

.done-card .btn-primary {
  margin-top: 16px;
}

.review-link-box {
  background: #f0f7f5;
  border-left: 4px solid var(--aecom-earth-green);
  padding: 14px 18px;
  border-radius: 4px;
  margin: 20px 0;
  line-height: 1.6;
}

.review-url {
  color: var(--aecom-earth-green);
  word-break: break-all;
  font-size: 0.9rem;
}

.done-actions {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 16px;
}

/* --- SurveyJS theming (AECOM brand) --- */
:deep(.sd-root-modern) {
  --sjs-primary-backcolor: var(--aecom-earth-green);
  --sjs-primary-backcolor-dark: var(--aecom-midnight-teal);
  --sjs-primary-forecolor: #ffffff;
  background: transparent;
}

:deep(.sd-title),
:deep(.sd-question__title) {
  color: var(--aecom-midnight-teal) !important;
}

:deep(.sd-btn--action),
:deep(.sd-navigation__complete-btn) {
  background-color: var(--aecom-earth-green) !important;
}

/* Attribute-inventory matrix: keep names bold, definitions muted. */
:deep(.attr-def) {
  display: block;
  font-weight: 400;
  font-size: 0.82rem;
  color: var(--aecom-black-75, #646363);
  line-height: 1.35;
  margin-top: 2px;
}

:deep(.sd-table__cell--header) {
  text-align: center;
  font-size: 0.85rem;
}

:deep(.survey-intro),
:deep(.notice-info),
:deep(.notice-warn),
:deep(.licence-box) {
  padding: 14px 18px;
  border-radius: 4px;
  line-height: 1.6;
  margin: 8px 0;
}

:deep(.survey-intro) {
  background: #f8f9fa;
  border-left: 4px solid var(--aecom-lime-green);
  color: var(--aecom-midnight-teal);
}

:deep(.licence-box) {
  background: #f8f9fa;
  border: 1px solid var(--aecom-black-15, #e3e3e3);
  border-left: 4px solid var(--aecom-earth-green);
  max-height: 320px;
  overflow-y: auto;
}

:deep(.notice-info) {
  background: #f0f7f5;
  border-left: 4px solid var(--aecom-earth-green);
  color: var(--aecom-midnight-teal);
}

:deep(.notice-warn) {
  background: #fff3f3;
  border-left: 4px solid var(--aecom-coral-orange, #e52713);
  color: #b3200f;
}

:deep(.property-info) {
  margin-top: 8px;
  padding: 12px 16px;
  background: #f0f7f5;
  border-left: 4px solid var(--aecom-earth-green);
  border-radius: 4px;
  color: var(--aecom-midnight-teal);
}

:deep(.property-not-found) {
  margin-top: 8px;
  padding: 12px 16px;
  background: #fff3f3;
  border-left: 4px solid #cc3333;
  border-radius: 4px;
  color: #cc3333;
}

:deep(.property-loading) {
  margin-top: 8px;
  padding: 12px 16px;
  color: #666;
  font-style: italic;
}
</style>
